import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { Box, Skeleton, Typography, Stack } from "@mui/material";
import { Dot } from "../UIElements/svgIcons";
import { useNavigate } from "react-router-dom";
import { formatDate, formatTextType, limitToSeven } from "../../utils/url-id";
import { headerSty, contentSty } from "../table-styles";

export default function RequestTable({ data, loading, handleOpenModal }: any) {
  const theme = useTheme();
  const headStyles = { ...headerSty, color: theme.palette.neutral.main };
  const contentStyles = { ...contentSty };

  const [openRow, setOpenRow] = React.useState<number | null>(null); // Track which row is open

  return (
    <TableContainer
      component={Paper}
      sx={{
        background: theme.palette.common.white,
        boxShadow: "none",
        minHeight: 400,
      }}
    >
      <Table
        sx={{
          minWidth: 650,
          boxShadow: "none",
          my: 2,
          paddingRight: 2,
        }}
        aria-label="simple table"
      >
        <TableHead sx={{ paddingX: 2 }}>
          <TableRow>
            <TableCell sx={{ ...headStyles, width: "12%" }}>
              Employee ID
            </TableCell>
            <TableCell sx={{ ...headStyles }}>Title</TableCell>
            <TableCell sx={{ ...headStyles }}> Total days</TableCell>
            <TableCell sx={{ ...headStyles }}>Type</TableCell>
            <TableCell sx={{ ...headStyles }}>Start Date</TableCell>
            <TableCell sx={{ ...headStyles }}>End Date</TableCell>
            <TableCell sx={{ ...headStyles }}>Medic Name</TableCell>
            <TableCell sx={{ ...headStyles }}>Medic Type</TableCell>
            <TableCell sx={{ ...headStyles }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading &&
            data.map((row: any, index: number) => (
              <TableRow
                onClick={() => handleOpenModal(row)}
                key={row.medic_info.medic_id ?? row?._id}
              >
                <TableCell sx={contentStyles}>
                  {limitToSeven(row?.employee)}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>{row?.title}</TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  {row?.total_days ?? "-"}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  {row?.type ?? "-"}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  {formatDate(row?.start_date)}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  {formatDate(row?.end_date)}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  {formatTextType(row?.medic_info?.name)}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  {formatTextType(row?.medic_info?.medic_type)}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  <EmploymentEmpStatus status={row?.status} />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {data?.length === 0 && !loading && (
        <Box
          sx={{
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
          >
            No Request Available.
          </Typography>
        </Box>
      )}
      {loading && (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
        </Box>
      )}
    </TableContainer>
  );
}

export const EmploymentEmpStatus: React.FC<{
  status: "approve" | "disapprove" | string;
}> = ({ status }) => {
  const theme = useTheme();

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "disapproved":
        return {
          color: theme.palette.error.main,
          backgroundColor: "#f6685e6b",
        };
      case "approved":
        return {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.lightBlue.main,
        };
      default:
        return {
          color: theme.palette.common.white,
          backgroundColor: theme.palette.warning.light,
        };
    }
  };

  const statusStyles = getStatusStyles(status);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ width: 100, mx: "auto" }}
    >
      <Box
        sx={{
          marginX: 1,
          paddingY: 1,
          paddingX: 3,
          borderRadius: "3px",
          fontSize: 12,
          fontFamily: "Lexend",
          textTransform: "uppercase",
          ...statusStyles,
        }}
      >
        {status}
      </Box>
    </Stack>
  );
};

const TableDots: React.FC<{
  rowId: number;
  isOpen: boolean;
  toggleOpen: (id: number) => void;
}> = ({ rowId, isOpen, toggleOpen }) => {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => {
    setOpen(!open);
  };
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={handleOpen}
        sx={{
          cursor: "pointer",
          width: "20px",
        }}
      >
        <Dot />
      </Box>
      {open && (
        <Box
          sx={{
            background: "#b0bee3",
            position: "absolute",
            top: 18,
            right: 0,
            borderRadius: "12px",
            padding: "8px 12px",
            zIndex: 10,
            width: 95,
            cursor: "pointer",
          }}
          onClick={() => navigate(`/employment/${rowId}`)}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#1E1E1E",
            }}
          >
            View more
          </Typography>
        </Box>
      )}
    </Box>
  );
};
